import React, { useState, useEffect } from 'react';
import { createTimesheet, updateTimesheet } from '../../services/timesheet.service';
import { useMessage } from '../../contexts/MessageContext';
import Loading from '../common/Loading';

const emptyForm = {
  work_date: '',
  start_time: '',
  end_time: '',
  break_minutes: 0,
  notes: '',
  status: 'draft',
};

function formatDateInput(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

const TimesheetForm = ({ entry, onSuccess, onCancel }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useMessage();

  useEffect(() => {
    if (entry) {
      setForm({
        work_date: formatDateInput(entry.work_date),
        start_time: entry.start_time ? entry.start_time.slice(0, 5) : '',
        end_time: entry.end_time ? entry.end_time.slice(0, 5) : '',
        break_minutes: entry.break_minutes || 0,
        notes: entry.notes || '',
        status: entry.status || 'draft',
      });
    } else {
      setForm(emptyForm);
    }
  }, [entry]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value === '' ? 0 : Number(value) }));
  };

  const validate = () => {
    if (!form.work_date) return 'Work date is required';
    if (!form.start_time) return 'Start time is required';
    if (!form.end_time) return 'End time is required';
    if (form.start_time >= form.end_time) return 'End time must be after start time';
    if (Number(form.break_minutes) < 0) return 'Break minutes cannot be negative';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      showError(error);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        work_date: form.work_date,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: Number(form.break_minutes) || 0,
        notes: form.notes,
        status: form.status,
      };

      if (entry?.uuid) {
        await updateTimesheet(entry.uuid, payload);
        showSuccess('Timesheet updated');
      } else {
        await createTimesheet(payload);
        showSuccess('Timesheet created');
      }
      setForm(emptyForm);
      onSuccess();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Failed to save timesheet');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4">
        {entry ? 'Edit Timesheet' : 'New Timesheet Entry'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            name="work_date"
            value={form.work_date}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
          <input
            type="time"
            name="start_time"
            value={form.start_time}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
          <input
            type="time"
            name="end_time"
            value={form.end_time}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Break (minutes)</label>
          <input
            type="number"
            name="break_minutes"
            min="0"
            value={form.break_minutes}
            onChange={handleNumberChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <input
            type="text"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder="What did you work on?"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
        >
          {entry ? 'Update' : 'Save'} Entry
        </button>
        {entry && (
          <button
            type="button"
            onClick={() => {
              setForm(emptyForm);
              onCancel();
            }}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 font-medium"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default TimesheetForm;
